import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import * as moment from 'moment';
import { combineLatest, of, ReplaySubject, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ContactPipe } from 'src/app/lib/customer/pipes/contact.pipe';
import { IssueAuditStatus } from '../../../classes';
import { IIssueAuditWrapperProvider, ISSUE_AUDIT_WRAPPER_PROVIDER } from '../../../interfaces';
import { IssueCategoryPipe } from '../../../pipes/issue-category.pipe';
import { IssuePriorityPipe } from '../../../pipes/issue-priority.pipe';
import { IssueStatusPipe } from '../../../../shared/pipes/issue-status.pipe';

@Component({
  selector: 'app-issue-status-diff',
  templateUrl: './issue-status-diff.component.html',
  styleUrls: ['./issue-status-diff.component.css'],
  providers: [
    ContactPipe,
    IssueCategoryPipe,
    IssueStatusPipe,
    IssuePriorityPipe
  ]
})
export class IssueStatusDiffComponent implements OnDestroy, OnInit {

  private _issueStatus = new ReplaySubject<IssueAuditStatus[]>();
  @Input() set issueStatus(issueStatus: IssueAuditStatus[]){
    if(!Array.isArray(issueStatus)) return;
    let stack = [...issueStatus.slice(1)], current = issueStatus[0], sorted: IssueAuditStatus[] = [];
    while(current){
      let aIndex = sorted.findIndex(x => x.guid === current.anchestorId);
      if(aIndex === -1){
        aIndex = sorted.findIndex(x => x.anchestorId === current.guid);
        if(aIndex === -1){
          aIndex= 0;
        }
        sorted = [...sorted.slice(0, aIndex + 1), current, ...sorted.slice(aIndex + 1)]; 
      } else {
        sorted = [...sorted.slice(0, aIndex), current, ...sorted.slice(aIndex)]; 
      }
      current = stack[0];
      stack.splice(0, 1);
    }
    this._issueStatus.next(sorted);
  }
  issueStatus$ = this._issueStatus.asObservable();
  initialNote$ = this.issueStatus$.pipe(switchMap((status: IssueAuditStatus[]) => status.length === 0 || !this._issueAuditWrapperProvider? of(null): this._issueAuditWrapperProvider.getIssueStatus(status[status.length - 1].anchestorId).pipe(map((x: IssueAuditStatus) => x? x.issuenote: null))))
  latestNote$ = this._issueStatus.pipe(map((x: IssueAuditStatus[]) => x.length > 0? x[0].issuenote: null));

  notes = null;
  changeChains: { attribute: string, values: string[] }[] = [];

  private _subscriptions: Subscription[] = [];

  constructor(
    @Optional() @Inject(ISSUE_AUDIT_WRAPPER_PROVIDER) private _issueAuditWrapperProvider: IIssueAuditWrapperProvider,
    private _contactPipe: ContactPipe,
    private _issueCategoryPipe: IssueCategoryPipe,
    private _issueStatusPipe: IssueStatusPipe,
    private _issuePriorityPipe: IssuePriorityPipe
  ) { }

  ngOnDestroy(): void {
    for(let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    this._subscriptions.push(...[
      this.issueStatus$.subscribe(audit => this.validateChanges(audit))
    ]);
    if(this._issueAuditWrapperProvider) this._subscriptions.push(this._issueAuditWrapperProvider.issueAuditWrapper$.subscribe((wrapper) => {
      this.issueStatus = wrapper.issueStatus;
    }));
  }

  validateChanges(issueStatus: IssueAuditStatus[]){
    var values;
    this.changeChains = [];
    for(let attribute of this.issueAttributeMap){
      values = issueStatus.map(x => x[attribute.attribute]);
      attribute.valueProvider(values.filter(onlyUnique) as any[]).subscribe((result: string[]) => {
        this.changeChains.push({ attribute: attribute.pretty, values: result });
      });
    }
    let notes = issueStatus.map(x => x.note).filter(x => x? true: false);
    if(notes.length === 1) this.notes = notes;
    else if(notes.length > 1){
      this.notes = notes.join('\n----------\n');
    }
  }

  issueAttributeMap = [
    { pretty: 'Geschäftlicher Ansprechpartner', attribute: 'commercialcontactpersonalid', valueProvider: (values: number[]) => combineLatest(values.map(x => this._contactPipe.transform(x))) },
    { pretty: 'Ansprechpartner vor Ort', attribute: 'localcontactpersonalid', valueProvider: (values: number[]) => combineLatest(values.map(x => this._contactPipe.transform(x))) },
    { pretty: 'Beschreibung', attribute: 'issuedesc', valueProvider: (values: string[]) => combineLatest(values.map(x => of(x))) },
    { pretty: 'Status', attribute: 'issuestate', valueProvider: (values: number[]) => combineLatest(values.map(y => this._issueStatusPipe.transform(y))) },
    { pretty: 'Kategorie', attribute: 'issuecategory', valueProvider: (values: number[]) => combineLatest(values.map(y => this._issueCategoryPipe.transform(y))) },
    { pretty: 'Priorität', attribute: 'issuepriority', valueProvider: (values: number[]) => combineLatest(values.map(y => this._issuePriorityPipe.transform(y))) },
    { pretty: 'Wunschdatum', attribute: 'issueescalation', valueProvider: (values: string[]) => combineLatest(values.map(x => of(moment(x).format('DD.MM.YYYY')))) },
    { pretty: 'Titel', attribute: 'issuetitle', valueProvider: (values: string[]) => combineLatest(values.map(x => of(x))) },
  ];

}


function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}
